import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Mention from '@tiptap/extension-mention';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import clsx from 'clsx';
import { Bold, Code, Heading1, Heading2, Italic, Link2, List, ListChecks, ListOrdered, Quote, Strikethrough, Underline as UnderlineIcon } from '@/components/ui/icons';
import { getDb } from '@/lib/store';
import { Avatar } from '@/components/ui/Avatar';

export interface RichEditorHandle {
  focus: () => void;
  clear: () => void;
  getHTML: () => string;
  isEmpty: () => boolean;
  editor: Editor | null;
}

interface MentionItem {
  id: string;
  label: string;
}

interface MentionState {
  items: MentionItem[];
  index: number;
  rect: DOMRect | null;
  command: ((item: MentionItem) => void) | null;
}

function MentionPopup({ state, onPick, onHover }: { state: MentionState; onPick: (i: number) => void; onHover: (i: number) => void }) {
  if (!state.rect || !state.command) return null;
  // flip above the caret when there isn't room below (e.g. bottom-docked composers)
  const estHeight = 44 + Math.max(1, state.items.length) * 32;
  const below = window.innerHeight - state.rect.bottom > estHeight + 12;
  const pos: React.CSSProperties = below ? { top: state.rect.bottom + 6 } : { bottom: window.innerHeight - state.rect.top + 6 };
  const left = Math.max(8, Math.min(state.rect.left, window.innerWidth - 248));
  return createPortal(
    <div className="popover mention-popup" style={{ position: 'fixed', left, width: 240, ...pos }}>
      <div className="menu">
        <div className="menu-label">People</div>
        {state.items.length === 0 && <div className="menu-empty">No matches</div>}
        {state.items.map((it, i) => (
          <button key={it.id} className={clsx('menu-item', { active: i === state.index })} onMouseEnter={() => onHover(i)} onMouseDown={(e) => (e.preventDefault(), onPick(i))}>
            <Avatar userId={it.id} size={22} />
            <span className="menu-item-label truncate">{it.label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const btn = (active: boolean, onClick: () => void, icon: React.ReactNode, title: string) => (
    <button
      type="button"
      title={title}
      className={clsx('rte-tool', { on: active })}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {icon}
    </button>
  );
  return (
    <div className="rte-toolbar">
      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), <Heading1 size={15} />, 'Heading 1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <Heading2 size={15} />, 'Heading 2')}
      <span className="rte-sep" />
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), <Bold size={15} />, 'Bold')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), <Italic size={15} />, 'Italic')}
      {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), <UnderlineIcon size={15} />, 'Underline')}
      {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), <Strikethrough size={15} />, 'Strikethrough')}
      {btn(editor.isActive('code'), () => editor.chain().focus().toggleCode().run(), <Code size={15} />, 'Code')}
      <span className="rte-sep" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), <List size={15} />, 'Bulleted list')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), <ListOrdered size={15} />, 'Numbered list')}
      {btn(editor.isActive('taskList'), () => editor.chain().focus().toggleTaskList().run(), <ListChecks size={15} />, 'Checklist')}
      {btn(editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run(), <Quote size={15} />, 'Quote')}
      {btn(
        editor.isActive('link'),
        () => {
          const prev = editor.getAttributes('link').href as string | undefined;
          const url = window.prompt('Link URL', prev ?? 'https://');
          if (url === null) return;
          if (url === '') editor.chain().focus().unsetLink().run();
          else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        },
        <Link2 size={15} />,
        'Link',
      )}
    </div>
  );
}

export const RichEditor = forwardRef<
  RichEditorHandle,
  {
    content: string;
    onChange?: (html: string) => void;
    onBlur?: (html: string) => void;
    onSubmit?: (html: string) => void;
    placeholder?: string;
    editable?: boolean;
    autofocus?: boolean;
    toolbar?: 'fixed' | 'none';
    className?: string;
    submitOnEnter?: boolean;
  }
>(function RichEditor({ content, onChange, onBlur, onSubmit, placeholder, editable = true, autofocus, toolbar = 'none', className, submitOnEnter }, ref) {
  const [mention, setMention] = useState<MentionState>({ items: [], index: 0, rect: null, command: null });
  const mentionRef = useRef(mention);
  mentionRef.current = mention;
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;

  const extensions = useMemo(
    () => [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: true, autolink: true, HTMLAttributes: { rel: 'noreferrer', target: '_blank' } }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        renderHTML: ({ node }) => ['span', { class: 'mention', 'data-id': node.attrs.id, 'data-type': 'mention' }, `@${node.attrs.label ?? node.attrs.id}`],
        suggestion: {
          items: ({ query }: { query: string }) =>
            Object.values(getDb().users)
              .filter((u) => u.name.toLowerCase().includes(query.toLowerCase()))
              .slice(0, 8)
              .map((u) => ({ id: u.id, label: u.name })),
          render: () => ({
            onStart: (p: SuggestionProps<MentionItem>) =>
              setMention({ items: p.items, index: 0, rect: p.clientRect?.() ?? null, command: (it) => p.command(it) }),
            onUpdate: (p: SuggestionProps<MentionItem>) =>
              setMention((m) => ({ ...m, items: p.items, index: Math.min(m.index, Math.max(0, p.items.length - 1)), rect: p.clientRect?.() ?? null, command: (it) => p.command(it) })),
            onKeyDown: ({ event }: SuggestionKeyDownProps) => {
              const m = mentionRef.current;
              if (!m.command) return false;
              if (event.key === 'ArrowDown') {
                setMention({ ...m, index: (m.index + 1) % Math.max(1, m.items.length) });
                return true;
              }
              if (event.key === 'ArrowUp') {
                setMention({ ...m, index: (m.index - 1 + m.items.length) % Math.max(1, m.items.length) });
                return true;
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                const it = m.items[m.index];
                if (it) m.command(it);
                return true;
              }
              if (event.key === 'Escape') {
                setMention({ items: [], index: 0, rect: null, command: null });
                return true;
              }
              return false;
            },
            onExit: () => setMention({ items: [], index: 0, rect: null, command: null }),
          }),
        },
      }),
    ],
    [placeholder],
  );

  const editor = useEditor({
    extensions,
    content,
    editable,
    autofocus: autofocus ? 'end' : false,
    editorProps: {
      attributes: { class: 'rte-content' },
      handleKeyDown: (_view, event) => {
        if (mentionRef.current.command) return false;
        const submitCombo = submitOnEnter ? event.key === 'Enter' && !event.shiftKey : event.key === 'Enter' && (event.ctrlKey || event.metaKey);
        if (submitCombo && submitRef.current) {
          event.preventDefault();
          submitRef.current(editorRef.current?.getHTML() ?? '');
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    onBlur: ({ editor }) => onBlur?.(editor.getHTML()),
  });
  const editorRef = useRef<Editor | null>(null);
  editorRef.current = editor;

  // external content changes (remote edits) when not focused
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    if (editor.getHTML() !== content) editor.commands.setContent(content || '', false);
  }, [content, editor]);

  useEffect(() => {
    editor?.setEditable(editable, false);
  }, [editable, editor]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => editor?.commands.focus('end'),
      clear: () => editor?.commands.clearContent(true),
      getHTML: () => editor?.getHTML() ?? '',
      isEmpty: () => editor?.isEmpty ?? true,
      editor,
    }),
    [editor],
  );

  return (
    <div className={clsx('rte', className)}>
      {toolbar === 'fixed' && editor && editable && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />
      <MentionPopup
        state={mention}
        onHover={(i) => setMention((m) => ({ ...m, index: i }))}
        onPick={(i) => {
          const it = mention.items[i];
          if (it && mention.command) mention.command(it);
        }}
      />
    </div>
  );
});

/** Static HTML renderer for stored rich text (comments, messages). */
export function RichText({ html, className }: { html: string; className?: string }) {
  return <div className={clsx('rte-content rte-static', className)} dangerouslySetInnerHTML={{ __html: sanitize(html) }} />;
}

/** Minimal sanitizer: strips scripts, event handlers and javascript: URLs. */
export function sanitize(html: string): string {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild!;
  root.querySelectorAll('script,style,iframe,object,embed,link,meta').forEach((n) => n.remove());
  root.querySelectorAll('*').forEach((el) => {
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) el.removeAttribute(attr.name);
      if ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(attr.value)) el.removeAttribute(attr.name);
    }
    if (el.tagName === 'A') {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noreferrer');
    }
  });
  return root.innerHTML;
}
